import { cn } from 'cn';
import { type ReactNode, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { type Option, ValidationError } from '@vestigas/ui/molecules';

import {
  costCenterCompanyNameOptionLabel,
  MultiSelectCostCenters,
} from '../SelectServerDriven';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

// Define a CostCenter type based on the expected structure; TODO: this should come as response type from the request.
type CostCenter = {
  id: string;
  name: string;
};

type P = {
  readonly classNameErrors?: string;
  readonly filterActive?: boolean | null;
  readonly formatOptionLabel?: (option: Option<CostCenter>) => ReactNode;
  readonly hasEmptyOption?: boolean;
  readonly hint?: string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<CostCenter>) => boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly testId?: string;
} & ComponentStyling;

export const MultiSelectCostCentersField = ({
  className,
  classNameErrors,
  filterActive = true,
  formatOptionLabel = costCenterCompanyNameOptionLabel,
  hint,
  hasEmptyOption = false,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  label,
  placeholder,
  style,
  testId,
}: P) => {
  const field = useFieldContext();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(field.state.meta.errors?.length);

  const id = useId();

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <MultiSelectCostCenters
        filterActive={filterActive}
        hasEmptyOption={hasEmptyOption}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMultiSelect={isMultiSelect}
        isRemovableValue={isRemovableValue}
        placeholder={placeholder}
        // testId={testId}
        value={field.state.value ?? []}
        onChange={(costCenters) => {
          const isArray = Array.isArray(costCenters);
          if (isArray) {
            field.handleChange(costCenters.map(({ id }) => id));
          } else {
            field.handleChange(costCenters?.id ?? '');
          }
        }}
      />
      {hasErrors ? (
        <ValidationError className={classNameErrors}>
          {unique(field.state.meta.errors.map(({ message }) => message)).join(
            ', ',
          )}
        </ValidationError>
      ) : null}
    </div>
  );
};
